import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Bot, ArrowRight, Check, MapPin, Building2, Users, 
  Phone, Clock, Star, TrendingUp, Zap, Shield,
  MessageSquare, Calendar, BarChart3, Globe, Mail
} from 'lucide-react';
import api from '@/services/api';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';
import { useSeoAbVariant } from '@/hooks/useSeoAbVariant';

// Location-specific hero images
const LOCATION_IMAGES = {
  'new-york': 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=1600&h=600&fit=crop',
  'los-angeles': 'https://images.unsplash.com/photo-1711470090808-d6297234fe92?w=1600&h=600&fit=crop',
  'chicago': 'https://images.unsplash.com/photo-1575380591643-b2c92368dc6d?w=1600&h=600&fit=crop',
  'houston': 'https://images.unsplash.com/photo-1591718029068-b6fd52fa93b3?w=1600&h=600&fit=crop',
  'miami': 'https://images.unsplash.com/photo-1633719479566-318c40e622ba?w=1600&h=600&fit=crop',
  'dallas': 'https://images.unsplash.com/photo-1563219125-60d10ffe8877?w=1600&h=600&fit=crop',
  'atlanta': 'https://images.unsplash.com/photo-1676329884494-5fdb8ff02f04?w=1600&h=600&fit=crop',
  'seattle': 'https://images.unsplash.com/photo-1502175353174-a7a70e73b362?w=1600&h=600&fit=crop',
  'denver': 'https://images.unsplash.com/photo-1602967689755-6bc05c2d3c5b?w=1600&h=600&fit=crop',
  'boston': 'https://images.unsplash.com/photo-1624979119080-320a79fc2146?w=1600&h=600&fit=crop',
  'phoenix': 'https://images.unsplash.com/photo-1645201296444-7afea92433f3?w=1600&h=600&fit=crop',
  'san-francisco': 'https://images.unsplash.com/photo-1495511167051-13bb07bde85b?w=1600&h=600&fit=crop',
  'austin': 'https://images.unsplash.com/photo-1618858146957-421f1af522a1?w=1600&h=600&fit=crop',
  'nashville': 'https://images.unsplash.com/photo-1608258598684-3118606cfdaa?w=1600&h=600&fit=crop',
  'las-vegas': 'https://images.unsplash.com/photo-1671573576055-b6ac57f8ec51?w=1600&h=600&fit=crop',
  'london': 'https://images.unsplash.com/photo-1654017324532-e9882c17db3f?w=1600&h=600&fit=crop',
  'toronto': 'https://images.unsplash.com/photo-1668882698355-923d532fa985?w=1600&h=600&fit=crop',
  'sydney': 'https://images.unsplash.com/photo-1523059623039-a9ed027e7fad?w=1600&h=600&fit=crop',
  'dubai': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1600&h=600&fit=crop',
  'singapore': 'https://images.unsplash.com/photo-1686455746285-4a921419bc6c?w=1600&h=600&fit=crop',
};

// Default fallback image for cities without a specific image
const DEFAULT_CITY_IMAGE = 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=1600&h=600&fit=crop';

// Location data for major US cities and regions - Industry-focused, region-specific content
const LOCATIONS = {
  'new-york': {
    city: 'New York',
    state: 'NY',
    region: 'Northeast',
    population: '8.3M',
    businesses: '230K+',
    headline: 'AI-Powered Client Intake for NYC\'s Financial & Legal Hub',
    subheadline: 'Wall Street law firms and Midtown financial advisors use Harmonix to qualify high-value clients, schedule consultations, and capture leads from international time zones—without missing a single after-hours inquiry.',
    localStats: {
      avgMessageVolume: '2,500+',
      missedRevenue: '$15,000/mo',
      avgResponseTime: '45 min'
    },
    industries: ['Investment Banking', 'Corporate Law', 'Private Healthcare', 'Luxury Real Estate', 'Media & Publishing'],
    industryDetails: [
      { name: 'Financial Services', challenge: 'High-net-worth clients expect immediate responses. Markets operate globally.', solution: 'AI qualifies prospects based on AUM, handles compliance-aware intake, routes to appropriate advisors.' },
      { name: 'Law Firms', challenge: 'Client conflicts, intake complexity, and billable hour pressures.', solution: 'Automated conflict checking prompts, intelligent case type routing, after-hours emergency triage.' },
      { name: 'Private Medical Practices', challenge: 'Concierge patients demand white-glove service around the clock.', solution: 'HIPAA-compliant AI handles appointment scheduling, insurance pre-verification questions, and urgent care routing.' }
    ],
    testimonial: {
      quote: "Our corporate law practice gets inquiries from Hong Kong at 2 AM and London at 5 AM. Harmonix ensures every potential M&A client gets immediate, professional engagement.",
      author: "Jennifer Walsh, Esq.",
      business: "Walsh & Partners LLP, Midtown Manhattan"
    }
  },
  'los-angeles': {
    city: 'Los Angeles',
    state: 'CA',
    region: 'West Coast',
    population: '3.9M',
    businesses: '180K+',
    headline: 'AI Assistant Built for LA\'s Entertainment & Wellness Economy',
    subheadline: 'From Beverly Hills plastic surgeons to Culver City production studios, LA businesses use Harmonix to manage high-touch client relationships across multiple time zones and demanding schedules.',
    localStats: {
      avgMessageVolume: '2,100+',
      missedRevenue: '$12,000/mo',
      avgResponseTime: '52 min'
    },
    industries: ['Plastic Surgery & Med Spas', 'Entertainment Production', 'Talent Management', 'Luxury Auto', 'Wellness & Fitness'],
    industryDetails: [
      { name: 'Cosmetic Surgery', challenge: 'Discretion-sensitive consultations, VIP patient expectations, complex scheduling.', solution: 'AI handles confidential inquiry intake, pre-consultation screening, and coordinates with patient coordinators.' },
      { name: 'Production Companies', challenge: 'Casting calls, vendor inquiries, and location scouts flood inboxes 24/7.', solution: 'Intelligent routing separates talent submissions, vendor quotes, and priority studio contacts.' },
      { name: 'Talent Agencies', challenge: 'Missing a casting director\'s call can cost clients career-defining roles.', solution: 'Priority caller identification, urgent callback flagging, and automated availability sharing.' }
    ],
    testimonial: {
      quote: "Our Beverly Hills practice sees patients from Dubai and Singapore. Harmonix handles time-zone-appropriate follow-ups and books consultations while we sleep.",
      author: "Dr. David Chen",
      business: "Pacific Aesthetics, Beverly Hills"
    }
  },
  'chicago': {
    city: 'Chicago',
    state: 'IL',
    region: 'Midwest',
    population: '2.7M',
    businesses: '95K+',
    headline: 'AI Customer Service for Chicago\'s Manufacturing & Professional Services',
    subheadline: 'From Loop law firms to suburban manufacturing plants, Chicago businesses rely on Harmonix to handle B2B inquiries, RFQ requests, and professional service intake with Midwest efficiency.',
    localStats: {
      avgMessageVolume: '1,800+',
      missedRevenue: '$10,500/mo',
      avgResponseTime: '38 min'
    },
    industries: ['Industrial Manufacturing', 'Commodities Trading', 'Insurance', 'Architecture & Engineering', 'Healthcare Systems'],
    industryDetails: [
      { name: 'Manufacturing', challenge: 'RFQs, distributor inquiries, and equipment service requests pile up.', solution: 'AI captures part numbers, quantities, and urgency levels—routes to sales or service teams appropriately.' },
      { name: 'Insurance Agencies', challenge: 'Claims, policy questions, and new quotes require different handling.', solution: 'Intelligent triage separates claims emergencies from quote requests, captures policy numbers automatically.' },
      { name: 'Architecture Firms', challenge: 'Project inquiries need qualification; existing client requests need priority routing.', solution: 'AI qualifies project scope and budget range, identifies existing clients, schedules appropriate meetings.' }
    ],
    testimonial: {
      quote: "We get RFQs from plant managers at 6 AM before their shifts start. Harmonix captures every detail so our sales team can respond by business hours.",
      author: "Robert Kowalski",
      business: "Precision Tool & Die, Elk Grove Village"
    }
  },
  'houston': {
    city: 'Houston',
    state: 'TX',
    region: 'South',
    population: '2.3M',
    businesses: '85K+',
    headline: 'AI Solutions for Houston\'s Energy Sector & Medical Center',
    subheadline: 'Energy companies and Texas Medical Center practices use Harmonix to manage 24/7 operations—from offshore rig emergencies to patient appointment scheduling across the world\'s largest medical complex.',
    localStats: {
      avgMessageVolume: '1,600+',
      missedRevenue: '$9,800/mo',
      avgResponseTime: '42 min'
    },
    industries: ['Oil & Gas Services', 'Petrochemical', 'Medical Specialists', 'Engineering Consulting', 'Commercial Real Estate'],
    industryDetails: [
      { name: 'Energy Services', challenge: 'Offshore operations run 24/7. Equipment failures can\'t wait until morning.', solution: 'AI triages emergency service calls, captures rig location and equipment details, escalates to on-call engineers.' },
      { name: 'Medical Specialists', challenge: 'International patients fly to Houston for specialized care. Coordination is complex.', solution: 'Multilingual AI handles international patient inquiries, insurance coordination, and travel-aware scheduling.' },
      { name: 'Engineering Consultants', challenge: 'Project-based work means constant new client acquisition and proposal management.', solution: 'AI qualifies project scope, captures technical requirements, and schedules discovery calls with appropriate specialists.' }
    ],
    testimonial: {
      quote: "When a platform in the Gulf has equipment issues at midnight, Harmonix ensures our service team gets the call within minutes, not hours.",
      author: "James Thompson",
      business: "Gulf Coast Industrial Services, Energy Corridor"
    }
  },
  'miami': {
    city: 'Miami',
    state: 'FL',
    region: 'Southeast',
    population: '470K',
    businesses: '45K+',
    headline: 'Bilingual AI Assistant for Miami\'s Latin American Gateway',
    subheadline: 'Miami\'s international business hub demands bilingual service. Harmonix handles English and Spanish inquiries seamlessly—from Brickell real estate to Little Havana healthcare, serving clients across the Americas.',
    localStats: {
      avgMessageVolume: '1,200+',
      missedRevenue: '$8,500/mo',
      avgResponseTime: '35 min'
    },
    industries: ['International Real Estate', 'Immigration Law', 'Private Banking', 'Luxury Hospitality', 'Medical Tourism'],
    industryDetails: [
      { name: 'International Real Estate', challenge: 'Latin American investors inquire in Spanish, often from different time zones.', solution: 'Bilingual AI qualifies buyers, captures investment criteria, schedules viewings in preferred language.' },
      { name: 'Immigration Law', challenge: 'Visa questions are urgent and clients often speak limited English.', solution: 'Spanish-fluent AI captures case details, explains process basics, books consultations with appropriate attorneys.' },
      { name: 'Medical Tourism', challenge: 'International patients need coordination across language, currency, and time zones.', solution: 'AI handles procedure inquiries in multiple languages, coordinates with patient concierges, manages follow-up.' }
    ],
    testimonial: {
      quote: "Half our real estate inquiries come from Bogotá and São Paulo. Harmonix handles them in Spanish and Portuguese, qualifying buyers before they even land in Miami.",
      author: "Carlos Mendez",
      business: "Brickell International Realty"
    }
  },
  'dallas': {
    city: 'Dallas',
    state: 'TX',
    region: 'South',
    population: '1.3M',
    businesses: '65K+',
    headline: 'AI-Powered Growth for DFW\'s Corporate Relocations & Tech Boom',
    subheadline: 'As Fortune 500 companies relocate to DFW, local service providers use Harmonix to capture the surge in corporate relocations, tech startup inquiries, and professional services demand.',
    localStats: {
      avgMessageVolume: '1,400+',
      missedRevenue: '$9,200/mo',
      avgResponseTime: '40 min'
    },
    industries: ['Corporate Relocation', 'Tech Startups', 'Commercial Banking', 'Executive Healthcare', 'Commercial Construction'],
    industryDetails: [
      { name: 'Relocation Services', challenge: 'Corporate HR departments need fast responses for relocating executives.', solution: 'AI captures relocation requirements, family needs, timeline urgency, and routes to appropriate specialists.' },
      { name: 'Tech Startups', challenge: 'Fast-growing companies need responsive vendors but can\'t afford full-time receptionists.', solution: 'Professional AI presence handles investor calls, customer support, and partnership inquiries.' },
      { name: 'Executive Healthcare', challenge: 'C-suite patients expect concierge-level service and schedule around board meetings.', solution: 'AI coordinates with executive assistants, offers flexible scheduling, handles last-minute changes professionally.' }
    ],
    testimonial: {
      quote: "When Goldman Sachs announced their Dallas expansion, our commercial real estate inquiries tripled overnight. Harmonix handled every one of them.",
      author: "Sarah Mitchell",
      business: "DFW Corporate Properties, Uptown Dallas"
    }
  },
  'atlanta': {
    city: 'Atlanta',
    state: 'GA',
    region: 'Southeast',
    population: '500K',
    businesses: '55K+',
    headline: 'AI Solutions for Atlanta\'s Film Industry & Regional HQ Economy',
    subheadline: 'Hollywood South production companies and newly relocated corporate headquarters use Harmonix to manage high-volume inquiries, vendor coordination, and professional services across the Southeast\'s business capital.',
    localStats: {
      avgMessageVolume: '1,100+',
      missedRevenue: '$7,800/mo',
      avgResponseTime: '36 min'
    },
    industries: ['Film & TV Production', 'Corporate Headquarters', 'Logistics & Supply Chain', 'Healthcare IT', 'Convention Services'],
    industryDetails: [
      { name: 'Film Production', challenge: 'Production schedules change hourly. Vendor and talent coordination is constant.', solution: 'AI handles location scout inquiries, equipment rental requests, and casting submission intake.' },
      { name: 'Corporate Services', challenge: 'Atlanta hosts 15+ Fortune 500 HQs, each with vendors competing for contracts.', solution: 'AI qualifies B2B inquiries, captures RFP requirements, and routes to business development teams.' },
      { name: 'Logistics Companies', challenge: 'Shipping inquiries arrive 24/7 from supply chain disruptions.', solution: 'AI captures shipment details, delivery urgency, and routes emergency requests to operations.' }
    ],
    testimonial: {
      quote: "During a Marvel production, we got 200+ vendor inquiries in one week. Harmonix qualified and routed every single one while we focused on filming.",
      author: "Marcus Johnson",
      business: "Peachtree Production Services, Midtown"
    }
  },
  'seattle': {
    city: 'Seattle',
    state: 'WA',
    region: 'Pacific Northwest',
    population: '750K',
    businesses: '48K+',
    headline: 'AI Customer Service for Seattle\'s Tech Giants & Coffee Culture',
    subheadline: 'From tech startup pitch decks to specialty coffee roaster inquiries, Seattle businesses use Harmonix to maintain their innovative, customer-first reputation while scaling operations.',
    localStats: {
      avgMessageVolume: '1,300+',
      missedRevenue: '$11,000/mo',
      avgResponseTime: '32 min'
    },
    industries: ['Cloud Computing', 'Biotech & Life Sciences', 'Specialty Coffee & Food', 'Outdoor Recreation', 'Sustainable Tech'],
    industryDetails: [
      { name: 'Tech Companies', challenge: 'Engineers want to code, not answer phones. But customers need support.', solution: 'AI handles tier-1 support, captures bug reports with technical details, escalates appropriately.' },
      { name: 'Biotech Startups', challenge: 'Investor relations, partnership inquiries, and regulatory questions overlap.', solution: 'AI routes investor calls to appropriate team members, captures partnership interest with relevant details.' },
      { name: 'Specialty Food/Coffee', challenge: 'Wholesale inquiries, retail orders, and distribution requests mix together.', solution: 'AI separates B2B from B2C, captures volume requirements, and qualifies wholesale accounts.' }
    ],
    testimonial: {
      quote: "Our roastery gets wholesale inquiries from Tokyo at 4 AM Seattle time. Harmonix captures their volume needs so we can respond with a proper quote by morning.",
      author: "Kevin Park",
      business: "Capitol Hill Coffee Roasters"
    }
  },
  'denver': {
    city: 'Denver',
    state: 'CO',
    region: 'Mountain West',
    population: '715K',
    businesses: '42K+',
    headline: 'AI Assistant for Denver\'s Outdoor Industry & Cannabis Economy',
    subheadline: 'From ski resorts to dispensaries, Denver businesses operate in heavily regulated industries with seasonal demand spikes. Harmonix handles compliance-aware customer service year-round.',
    localStats: {
      avgMessageVolume: '950+',
      missedRevenue: '$7,200/mo',
      avgResponseTime: '34 min'
    },
    industries: ['Outdoor Recreation & Ski', 'Cannabis Retail & B2B', 'Craft Brewing', 'Adventure Tourism', 'Renewable Energy'],
    industryDetails: [
      { name: 'Ski & Recreation', challenge: 'Booking spikes in November-March, with complex equipment rental coordination.', solution: 'AI handles reservation inquiries, captures group sizes and skill levels, books appropriate packages.' },
      { name: 'Cannabis Industry', challenge: 'Strict compliance requirements. No minors. Inventory questions are constant.', solution: 'AI verifies age compliance language, handles product availability questions within regulations.' },
      { name: 'Craft Brewing', challenge: 'Taproom hours, event bookings, and distribution inquiries overlap.', solution: 'AI separates consumer from wholesale inquiries, handles event reservations, captures distributor interest.' }
    ],
    testimonial: {
      quote: "Ski season hits and suddenly we're drowning in booking requests. Harmonix handled our entire holiday rush while we focused on guest experience.",
      author: "Lisa Chen",
      business: "Rocky Mountain Ski & Board, Winter Park"
    }
  },
  'boston': {
    city: 'Boston',
    state: 'MA',
    region: 'Northeast',
    population: '690K',
    businesses: '38K+',
    headline: 'AI Solutions for Boston\'s Biotech Corridor & Academic Medical Centers',
    subheadline: 'Kendall Square biotech firms and Harvard-affiliated medical practices use Harmonix to manage complex scientific inquiries, patient referrals, and research partnerships with academic precision.',
    localStats: {
      avgMessageVolume: '1,150+',
      missedRevenue: '$10,500/mo',
      avgResponseTime: '38 min'
    },
    industries: ['Biotechnology', 'Academic Medicine', 'Venture Capital', 'University Services', 'Pharmaceutical'],
    industryDetails: [
      { name: 'Biotech Companies', challenge: 'Partnership inquiries mix with job applicants and investor relations.', solution: 'AI categorizes scientific inquiries, captures credentials for partnership discussions, routes appropriately.' },
      { name: 'Academic Medical', challenge: 'Complex referral networks, research study inquiries, and patient scheduling.', solution: 'AI handles study enrollment questions, captures referral details, coordinates with academic coordinators.' },
      { name: 'VC Firms', challenge: 'Hundreds of pitch requests, portfolio company needs, and LP communications.', solution: 'AI screens pitch submissions, captures key metrics, routes hot opportunities to partners.' }
    ],
    testimonial: {
      quote: "We get partnership inquiries from pharma companies worldwide. Harmonix captures the scientific details so our BD team can respond intelligently.",
      author: "Dr. Robert Kim",
      business: "Kendall Therapeutics, Cambridge"
    }
  },
  'phoenix': {
    city: 'Phoenix',
    state: 'AZ',
    region: 'Southwest',
    population: '1.6M',
    businesses: '72K+',
    headline: 'AI Service for Phoenix\'s Retirement & HVAC Emergency Economy',
    subheadline: 'The Valley of the Sun serves America\'s largest retirement migration and battles 115°F summers. Harmonix helps senior-focused services and emergency HVAC companies handle surging demand.',
    localStats: {
      avgMessageVolume: '1,600+',
      missedRevenue: '$9,500/mo',
      avgResponseTime: '42 min'
    },
    industries: ['HVAC Services', 'Senior Healthcare', 'Pool Services', 'Retirement Communities', 'Solar Installation'],
    industryDetails: [
      { name: 'HVAC Companies', challenge: 'When it hits 115°F, AC failures are life-threatening emergencies for seniors.', solution: 'AI triages by urgency (senior/medical priority), captures equipment details, dispatches emergency crews.' },
      { name: 'Senior Services', challenge: 'Adult children often call on behalf of aging parents. Patience and clarity required.', solution: 'AI handles family member inquiries compassionately, captures detailed needs, schedules appropriate assessments.' },
      { name: 'Pool Services', challenge: 'Seasonal maintenance plus emergency repairs during monsoon season.', solution: 'AI handles routine scheduling, captures storm damage details, coordinates emergency pump-outs.' }
    ],
    testimonial: {
      quote: "During heat waves, we get 300+ emergency calls in a day. Harmonix triages by medical urgency so we reach vulnerable seniors first.",
      author: "Mike Patterson",
      business: "Desert Comfort HVAC, Scottsdale"
    }
  },
  'san-francisco': {
    city: 'San Francisco',
    state: 'CA',
    region: 'West Coast',
    population: '870K',
    businesses: '55K+',
    headline: 'AI Assistant for SF\'s Venture-Backed Startups & Tech Elite',
    subheadline: 'SOMA startups and Financial District wealth managers use Harmonix to project enterprise credibility and handle global inquiries—without the overhead of a full-time receptionist.',
    localStats: {
      avgMessageVolume: '1,400+',
      missedRevenue: '$14,000/mo',
      avgResponseTime: '32 min'
    },
    industries: ['Venture-Backed Startups', 'Wealth Management', 'Legal Tech', 'Healthtech', 'Climate Tech'],
    industryDetails: [
      { name: 'Startups', challenge: 'Need to appear professional to investors while keeping burn rate low.', solution: 'Enterprise-grade AI reception creates credibility without full-time overhead.' },
      { name: 'Wealth Management', challenge: 'Ultra-high-net-worth clients expect immediate, discreet service.', solution: 'AI handles client calls with appropriate gravitas, routes by relationship level, maintains confidentiality.' },
      { name: 'Legal Tech', challenge: 'Demos, support tickets, and enterprise inquiries need different handling.', solution: 'AI qualifies leads by company size, routes enterprise opportunities, handles support triage.' }
    ],
    testimonial: {
      quote: "We\'re a Series A startup but Harmonix makes us sound like we have a full operations team. Investors are impressed before they even meet us.",
      author: "Emily Zhang",
      business: "Quantum AI Labs, SOMA"
    }
  },
  'austin': {
    city: 'Austin',
    state: 'TX',
    region: 'South',
    population: '970K',
    businesses: '48K+',
    headline: 'AI Customer Service for Austin\'s Tech Migration & Music Scene',
    subheadline: 'As Tesla, Oracle, and thousands of California refugees reshape Austin, local businesses use Harmonix to capture the influx while maintaining that Keep Austin Weird authenticity.',
    localStats: {
      avgMessageVolume: '1,200+',
      missedRevenue: '$8,200/mo',
      avgResponseTime: '40 min'
    },
    industries: ['Tech Relocations', 'Live Music Venues', 'Food & Beverage', 'Coworking Spaces', 'Electric Vehicles'],
    industryDetails: [
      { name: 'Tech Companies', challenge: 'Rapid growth means hiring, vendor management, and customer support scale simultaneously.', solution: 'AI handles recruiting inquiries, vendor pitches, and customer support—routing each appropriately.' },
      { name: 'Music Venues', challenge: 'Booking inquiries, ticket questions, and artist management overlap constantly.', solution: 'AI separates fan inquiries from booking requests, captures artist availability, handles event info.' },
      { name: 'Food & Hospitality', challenge: 'Reservations, event bookings, and catering requests flood during SXSW and ACL.', solution: 'AI handles peak-volume reservations, captures large party details, coordinates event catering.' }
    ],
    testimonial: {
      quote: "During SXSW, we get more inquiries in one week than most of the year. Harmonix handled every single one while we focused on the shows.",
      author: "Tommy Garcia",
      business: "6th Street Live Music Hall"
    }
  },
  'nashville': {
    city: 'Nashville',
    state: 'TN',
    region: 'South',
    population: '690K',
    businesses: '32K+',
    headline: 'AI Solutions for Nashville\'s Healthcare Industry & Hospitality Boom',
    subheadline: 'America\'s healthcare management capital and the Bachelorette Party Capital of the World use Harmonix to handle professional medical inquiries and party planning chaos alike.',
    localStats: {
      avgMessageVolume: '950+',
      missedRevenue: '$6,800/mo',
      avgResponseTime: '42 min'
    },
    industries: ['Healthcare Management', 'Music Industry', 'Tourism & Hotels', 'Event Planning', 'Commercial Development'],
    industryDetails: [
      { name: 'Healthcare HQ', challenge: 'Nashville hosts HCA and 20+ healthcare company headquarters. B2B complexity is high.', solution: 'AI handles vendor inquiries, partnership discussions, and healthcare system coordination.' },
      { name: 'Tourism & Events', challenge: 'Broadway party buses, hotel blocks, and event coordination for thousands of visitors.', solution: 'AI captures group sizes, date requirements, and special requests—routing to event coordinators.' },
      { name: 'Music Business', challenge: 'Artist management, publishing, and label inquiries require industry knowledge.', solution: 'AI uses music industry terminology, captures demo submissions appropriately, routes to A&R.' }
    ],
    testimonial: {
      quote: "HCA generates hundreds of vendor inquiries daily. Harmonix qualifies them so our procurement team can focus on strategic partnerships.",
      author: "Brad Johnson",
      business: "Nashville Healthcare Consulting"
    }
  },
  'las-vegas': {
    city: 'Las Vegas',
    state: 'NV',
    region: 'Southwest',
    population: '640K',
    businesses: '38K+',
    headline: 'AI That Never Sleeps for Vegas\'s 24/7 Service Economy',
    subheadline: 'The city that never sleeps needs service providers that never sleep. Harmonix handles emergency plumbing at 3 AM, convention booth inquiries, and wedding vendor coordination around the clock.',
    localStats: {
      avgMessageVolume: '1,100+',
      missedRevenue: '$8,500/mo',
      avgResponseTime: '35 min'
    },
    industries: ['Casino & Hospitality', 'Convention Services', 'Wedding Planning', 'Emergency Services', 'Real Estate Investment'],
    industryDetails: [
      { name: 'Convention Services', challenge: 'CES generates more leads in 4 days than most businesses see in a year.', solution: 'AI handles booth inquiries, captures lead details at scale, schedules post-show follow-ups.' },
      { name: 'Wedding Industry', challenge: 'Couples in different time zones planning Vegas weddings need instant responses.', solution: 'AI handles package inquiries, captures wedding dates, coordinates with venue and vendor teams.' },
      { name: 'Emergency Services', challenge: 'Hotels and casinos need 24/7 plumbing, electrical, and HVAC support.', solution: 'AI dispatches emergency crews with property and problem details for immediate response.' }
    ],
    testimonial: {
      quote: "During CES, we captured 400+ leads in four days. Harmonix handled every single inquiry while we worked the show floor.",
      author: "Tony Martinez",
      business: "Vegas Convention Displays"
    }
  },
  // International Cities
  'london': {
    city: 'London',
    state: 'UK',
    region: 'Europe',
    population: '9.0M',
    businesses: '400K+',
    headline: 'AI-Powered Service for London\'s Financial & Legal Centre',
    subheadline: 'From City law firms to Canary Wharf hedge funds, London businesses use Harmonix to manage client inquiries across European, Asian, and American time zones with proper British professionalism.',
    localStats: {
      avgMessageVolume: '3,000+',
      missedRevenue: '£12,000/mo',
      avgResponseTime: '35 min'
    },
    industries: ['Financial Services', 'International Law', 'Private Healthcare', 'Property Investment', 'Fintech'],
    industryDetails: [
      { name: 'City Law Firms', challenge: 'International clients in multiple time zones expect immediate acknowledgment.', solution: 'AI handles client intake with appropriate formality, captures matter details, confirms conflict checks.' },
      { name: 'Hedge Funds', challenge: 'Investor relations, compliance inquiries, and operational needs overlap.', solution: 'AI routes appropriately—LP inquiries to IR, operational to ops, compliance to legal.' },
      { name: 'Harley Street Medical', challenge: 'International patients booking private consultations expect concierge service.', solution: 'AI handles multilingual inquiries, coordinates with patient liaisons, manages complex scheduling.' }
    ],
    testimonial: {
      quote: "Our clients ring from Singapore, New York, and Dubai. Harmonix ensures every inquiry receives immediate, professional acknowledgment regardless of the hour.",
      author: "James Crawford",
      business: "Crawford & Partners Solicitors, Canary Wharf"
    }
  },
  'toronto': {
    city: 'Toronto',
    state: 'Canada',
    region: 'North America',
    population: '2.9M',
    businesses: '180K+',
    headline: 'AI Assistant for Toronto\'s Banking District & Tech Corridor',
    subheadline: 'Bay Street\'s big five banks and Kitchener-Waterloo tech startups use Harmonix to deliver Canadian hospitality at scale—bilingual service in English and French across North America\'s most diverse city.',
    localStats: {
      avgMessageVolume: '2,200+',
      missedRevenue: '$14,000/mo CAD',
      avgResponseTime: '38 min'
    },
    industries: ['Banking & Insurance', 'Tech Startups', 'Healthcare', 'Real Estate Development', 'Film Production'],
    industryDetails: [
      { name: 'Financial Services', challenge: 'Canada\'s banks serve clients in both official languages across multiple time zones.', solution: 'Bilingual AI handles English and French inquiries, routes by region and service type.' },
      { name: 'Tech Companies', challenge: 'Toronto competes with Silicon Valley for talent. First impressions matter.', solution: 'Professional AI creates enterprise presence for startups competing for top engineering talent.' },
      { name: 'Real Estate', challenge: 'International buyers from Hong Kong and mainland China drive luxury market.', solution: 'AI handles inquiries in multiple languages, captures investment criteria, coordinates viewings.' }
    ],
    testimonial: {
      quote: "We serve clients from Vancouver to Halifax in both English and French. Harmonix handles bilingual intake flawlessly.",
      author: "Dr. Sarah Chen",
      business: "Bay Street Medical Associates"
    }
  },
  'dubai': {
    city: 'Dubai',
    state: 'UAE',
    region: 'Middle East',
    population: '3.5M',
    businesses: '300K+',
    headline: 'AI Excellence for Dubai\'s Luxury & International Business Hub',
    subheadline: 'The world comes to Dubai for luxury real estate, medical tourism, and business expansion. Harmonix helps service providers meet the ultra-high expectations of a global clientele across all time zones.',
    localStats: {
      avgMessageVolume: '2,000+',
      missedRevenue: '$10,000/mo',
      avgResponseTime: '30 min'
    },
    industries: ['Luxury Real Estate', 'Medical Tourism', 'Wealth Management', 'Hospitality', 'Free Zone Services'],
    industryDetails: [
      { name: 'Luxury Property', challenge: 'Russian, Chinese, and European buyers expect immediate service in their language.', solution: 'Multilingual AI handles inquiries in multiple languages, qualifies by budget and visa status.' },
      { name: 'Medical Tourism', challenge: 'Patients travel from across the region for procedures. Coordination is complex.', solution: 'AI handles procedure inquiries, coordinates with patient liaisons, manages visa letter requirements.' },
      { name: 'Free Zone Services', challenge: 'Company formation inquiries come from entrepreneurs worldwide around the clock.', solution: 'AI captures business type, nationality, and requirements for appropriate free zone recommendations.' }
    ],
    testimonial: {
      quote: "Our clients fly in from Moscow, Mumbai, and Manchester. Harmonix ensures every inquiry gets immediate attention in their preferred language.",
      author: "Ahmed Al-Rashid",
      business: "Emirates Premium Properties, Downtown Dubai"
    }
  },
  'singapore': {
    city: 'Singapore',
    state: 'Singapore',
    region: 'Asia Pacific',
    population: '5.9M',
    businesses: '280K+',
    headline: 'AI Solutions for Singapore\'s Regional Headquarters & Financial Centre',
    subheadline: 'Asia\'s business hub hosts regional headquarters for global companies. Harmonix helps Singapore businesses serve clients from Tokyo to Sydney with the efficiency the Lion City demands.',
    localStats: {
      avgMessageVolume: '1,500+',
      missedRevenue: '$9,500/mo SGD',
      avgResponseTime: '32 min'
    },
    industries: ['Regional Headquarters', 'Private Banking', 'Shipping & Logistics', 'Legal Services', 'Healthcare'],
    industryDetails: [
      { name: 'Regional HQ', challenge: 'Singapore offices coordinate across APAC time zones from Tokyo to Perth.', solution: 'AI handles inquiries across time zones, routes to appropriate regional teams, captures country-specific needs.' },
      { name: 'Private Wealth', challenge: 'Ultra-high-net-worth families expect discretion and immediate service.', solution: 'AI handles client communications with appropriate discretion, routes by relationship manager.' },
      { name: 'Shipping & Trade', challenge: 'The world\'s busiest port generates constant documentation and inquiry volume.', solution: 'AI captures shipment details, routes urgent matters, handles documentation requests.' }
    ],
    testimonial: {
      quote: "We coordinate with offices in 12 countries. Harmonix ensures nothing falls through the cracks across time zones.",
      author: "Dr. Wei Lin",
      business: "APAC Medical Holdings, Orchard Road"
    }
  },
  'sydney': {
    city: 'Sydney',
    state: 'Australia',
    region: 'Asia Pacific',
    population: '5.3M',
    businesses: '250K+',
    headline: 'AI Customer Service for Sydney\'s Professional Services & Tourism',
    subheadline: 'From Barangaroo\'s financial towers to Bondi\'s surf schools, Sydney businesses use Harmonix to manage professional inquiries and tourism bookings across Australia\'s time zone isolation.',
    localStats: {
      avgMessageVolume: '1,800+',
      missedRevenue: '$11,000/mo AUD',
      avgResponseTime: '40 min'
    },
    industries: ['Financial Services', 'Legal', 'Tourism & Hospitality', 'Healthcare', 'Education'],
    industryDetails: [
      { name: 'Professional Services', challenge: 'Sydney\'s time zone means European and American clients call outside business hours.', solution: 'AI handles after-hours inquiries professionally, captures details, schedules callbacks in appropriate hours.' },
      { name: 'Tourism', challenge: 'International tourists book activities, accommodation, and experiences around the clock.', solution: 'AI handles booking inquiries in multiple languages, captures group details, confirms reservations.' },
      { name: 'Healthcare', challenge: 'Private healthcare serves both local and medical tourism patients.', solution: 'AI handles domestic and international patient inquiries, coordinates with patient liaisons.' }
    ],
    testimonial: {
      quote: "Our London office calls at 2 AM Sydney time. Harmonix ensures they get immediate acknowledgment and we respond properly by morning.",
      author: "Michael O\'Brien",
      business: "Sydney Corporate Advisors, Barangaroo"
    }
  },
  'mumbai': {
    city: 'Mumbai',
    state: 'India',
    region: 'Asia Pacific',
    population: '21M',
    businesses: '500K+',
    headline: 'AI Solutions for Mumbai\'s Financial Capital & Entertainment Industry',
    subheadline: 'Dalal Street\'s trading floors and Bollywood\'s production houses use Harmonix to manage the highest volume of business inquiries in India—across time zones from New York to Singapore.',
    localStats: {
      avgMessageVolume: '3,500+',
      missedRevenue: '₹8,00,000/mo',
      avgResponseTime: '45 min'
    },
    industries: ['Financial Services', 'Entertainment & Film', 'IT Services', 'Pharmaceuticals', 'Textile & Fashion'],
    industryDetails: [
      { name: 'Financial Markets', challenge: 'Global investors need service during their market hours, not Mumbai\'s.', solution: 'AI handles international investor inquiries 24/7, captures investment criteria, routes to relationship managers.' },
      { name: 'Film Production', challenge: 'Bollywood produces 1,500+ films yearly. Coordination chaos is constant.', solution: 'AI handles casting submissions, location requests, and equipment inquiries—routing appropriately.' },
      { name: 'IT Services', challenge: 'Global clients in every time zone need support and escalation paths.', solution: 'AI provides first-level triage, captures issue details, escalates to appropriate support tiers.' }
    ],
    testimonial: {
      quote: "We serve global clients from Wall Street to Singapore. Harmonix ensures every inquiry gets professional handling regardless of the hour.",
      author: "Dr. Priya Sharma",
      business: "BKC Financial Advisors, Bandra Kurla Complex"
    }
  },
  'philadelphia': {
    city: 'Philadelphia',
    state: 'PA',
    region: 'Northeast',
    population: '1.6M',
    businesses: '68K+',
    headline: 'AI Assistant for Philadelphia\'s Healthcare Network & Higher Education',
    subheadline: 'University City\'s world-class hospitals and Center City\'s professional services use Harmonix to manage patient referrals, research inquiries, and academic-medical coordination across the largest healthcare economy in the Mid-Atlantic.',
    localStats: { avgMessageVolume: '1,400+', missedRevenue: '$9,200/mo', avgResponseTime: '40 min' },
    industries: ['Hospital Systems', 'Higher Education', 'Biotech Research', 'Legal Services', 'Financial Services'],
    industryDetails: [
      { name: 'Academic Medicine', challenge: 'Penn Medicine and CHOP field constant referral requests and research study inquiries.', solution: 'AI categorises referrals by specialty, captures insurance details, and routes study enrollment to coordinators.' },
      { name: 'Universities', challenge: 'Prospective students, research partners, and donor inquiries come from every time zone.', solution: 'AI routes admissions questions, captures partnership details, and handles donor relations with appropriate tone.' },
      { name: 'Law Firms', challenge: 'Center City firms balance corporate matters with personal injury and insurance work.', solution: 'AI qualifies matter type, runs conflict-check prompts, and books intake calls with appropriate attorneys.' }
    ],
    testimonial: {
      quote: "Our practice receives referrals from rural Pennsylvania to the Jersey Shore. Harmonix captures every patient detail so we can respond with a proper care plan.",
      author: "Dr. Rachel Stein",
      business: "University City Medical Associates"
    }
  },
  'san-antonio': {
    city: 'San Antonio',
    state: 'TX',
    region: 'South',
    population: '1.5M',
    businesses: '52K+',
    headline: 'AI Service for San Antonio\'s Military Health & Tourism Economy',
    subheadline: 'From Joint Base San Antonio\'s medical command to River Walk hospitality, local businesses use Harmonix to serve military families, tourists, and booming south Texas commerce around the clock.',
    localStats: { avgMessageVolume: '1,000+', missedRevenue: '$7,000/mo', avgResponseTime: '38 min' },
    industries: ['Military Healthcare', 'Tourism & Hospitality', 'Cybersecurity', 'Energy Services', 'Manufacturing'],
    industryDetails: [
      { name: 'Military Health', challenge: 'TRICARE patients and military families have specific scheduling and referral requirements.', solution: 'AI understands military referral patterns, handles command coordination, and books around deployment schedules.' },
      { name: 'Tourism', challenge: 'Fiesta, Spurs games and convention bookings cause massive, unpredictable volume spikes.', solution: 'AI handles reservation requests at scale, captures group sizes, and confirms bookings in English and Spanish.' },
      { name: 'Cyber Industry', challenge: 'San Antonio is the nation\'s cyber capital — B2B inquiries require technical qualification.', solution: 'AI captures clearance requirements, contract vehicles, and technical scope, routing to solutions engineers.' }
    ],
    testimonial: {
      quote: "During Fiesta week we field ten times our normal volume. Harmonix never drops a booking and handles Spanish-speaking guests seamlessly.",
      author: "Maria Gonzalez",
      business: "River Walk Hospitality Group"
    }
  },
  'san-diego': {
    city: 'San Diego',
    state: 'CA',
    region: 'West Coast',
    population: '1.4M',
    businesses: '58K+',
    headline: 'AI Solutions for San Diego\'s Biotech, Defense & Cross-Border Economy',
    subheadline: 'Torrey Pines biotech, Navy contractors, and binational Tijuana commerce depend on Harmonix to manage bilingual inquiries, defense-grade professionalism, and 24/7 cross-border coordination.',
    localStats: { avgMessageVolume: '1,300+', missedRevenue: '$10,500/mo', avgResponseTime: '36 min' },
    industries: ['Biotech', 'Defense Contracting', 'Cross-Border Trade', 'Tourism', 'Action Sports'],
    industryDetails: [
      { name: 'Biotech & Life Sciences', challenge: 'Torrey Pines startups field pharma partnership, investor and hiring inquiries simultaneously.', solution: 'AI separates BD, IR and recruiting messages, captures scientific qualifications, and routes to the right leader.' },
      { name: 'Defense Contractors', challenge: 'DoD procurement and subcontractor inquiries require rapid, compliance-aware intake.', solution: 'AI captures contract numbers, clearance levels and response deadlines for priority routing.' },
      { name: 'Cross-Border Business', challenge: 'Tijuana manufacturing partners and logistics vendors prefer Spanish and operate on two time zones.', solution: 'Bilingual AI handles Spanish inquiries, captures customs and logistics details, routes urgent matters to ops.' }
    ],
    testimonial: {
      quote: "We receive partnership inquiries from Big Pharma at 5am Pacific. Harmonix captures every scientific detail so our BD team hits the ground running.",
      author: "Dr. Priya Venkat",
      business: "Torrey Pines Therapeutics"
    }
  },
  'san-jose': {
    city: 'San Jose',
    state: 'CA',
    region: 'West Coast',
    population: '1.0M',
    businesses: '45K+',
    headline: 'AI Infrastructure for San Jose\'s Silicon Valley Core',
    subheadline: 'Home to the hardware and semiconductor giants that power the tech industry, San Jose businesses use Harmonix to deliver enterprise-grade client service while their engineers focus on shipping.',
    localStats: { avgMessageVolume: '1,100+', missedRevenue: '$12,000/mo', avgResponseTime: '30 min' },
    industries: ['Semiconductors', 'Enterprise Software', 'Hardware Manufacturing', 'Venture Capital', 'EV & Clean Tech'],
    industryDetails: [
      { name: 'Semiconductor Firms', challenge: 'Global OEMs call about supply, pricing and roadmap with urgency that spans time zones.', solution: 'AI captures part numbers, volumes and target dates, routing procurement inquiries to field application engineers.' },
      { name: 'Enterprise SaaS', challenge: 'Demo requests, RFPs and support tickets overlap during aggressive sales cycles.', solution: 'AI qualifies by company size and use case, books demos on the right AE\'s calendar, and triages support.' },
      { name: 'Hardware Startups', challenge: 'Factory partners, component vendors and design firms need constant coordination.', solution: 'AI handles supplier inquiries, captures BOM details, and routes to operations for follow-up.' }
    ],
    testimonial: {
      quote: "Harmonix lets our 20-person chip startup look like a Fortune 500 to enterprise customers. Our reps only see qualified demos.",
      author: "Ken Tanaka",
      business: "Valley Silicon Labs, North San Jose"
    }
  },
  'portland': {
    city: 'Portland',
    state: 'OR',
    region: 'Pacific Northwest',
    population: '650K',
    businesses: '35K+',
    headline: 'AI Assistant for Portland\'s Sustainable Business & Athletic Apparel Hub',
    subheadline: 'Nike, Adidas North America, and hundreds of craft producers use Harmonix to handle wholesale, sustainability-focused inquiries, and the Pacific Northwest\'s distinctly values-driven customer base.',
    localStats: { avgMessageVolume: '900+', missedRevenue: '$6,500/mo', avgResponseTime: '34 min' },
    industries: ['Athletic Apparel', 'Craft Beverage', 'Sustainable Products', 'Software', 'Outdoor Recreation'],
    industryDetails: [
      { name: 'Apparel & Footwear', challenge: 'Wholesale accounts, influencer partnerships and consumer support all land in one inbox.', solution: 'AI routes B2B from B2C, captures account numbers and buyer info, qualifies influencer collabs for marketing.' },
      { name: 'Craft Food & Beverage', challenge: 'Distributor inquiries, taproom events and retail orders require completely different handling.', solution: 'AI separates wholesale from consumer, captures event details and volume needs for proper quoting.' },
      { name: 'Sustainable Brands', challenge: 'Customers expect detailed questions about sourcing, materials and certifications.', solution: 'AI handles common sustainability questions, captures detailed product inquiries, escalates to specialists.' }
    ],
    testimonial: {
      quote: "Our sustainable footwear brand got 50+ wholesale inquiries from Europe overnight. Harmonix captured every one ready for our reps by morning.",
      author: "Jessica Hart",
      business: "Cascade Footwear Co., SE Portland"
    }
  },
  'detroit': {
    city: 'Detroit',
    state: 'MI',
    region: 'Midwest',
    population: '640K',
    businesses: '28K+',
    headline: 'AI for Detroit\'s Auto Industry & Mobility Tech Renaissance',
    subheadline: 'From Big Three headquarters to Corktown mobility startups, Detroit businesses use Harmonix to manage supplier negotiations, dealer networks, and the city\'s resurgent tech economy.',
    localStats: { avgMessageVolume: '1,100+', missedRevenue: '$8,800/mo', avgResponseTime: '42 min' },
    industries: ['Automotive OEMs', 'Tier-1 Suppliers', 'Mobility Tech', 'Manufacturing', 'Commercial Real Estate'],
    industryDetails: [
      { name: 'Automotive Suppliers', challenge: 'OEM buyers expect 24/7 responsiveness on quality issues, delivery and new-program quoting.', solution: 'AI triages by urgency and program, captures part numbers and ship dates, escalates line-down issues.' },
      { name: 'Mobility Startups', challenge: 'Pilot partnerships with OEMs, city governments and fleets come with complex procurement cycles.', solution: 'AI qualifies pilot opportunities, captures stakeholders and timelines, routes to business development.' },
      { name: 'Dealer Networks', challenge: 'Service scheduling, parts inquiries and sales leads flood in from multiple channels.', solution: 'AI books service, captures VIN and issue details, and routes sales inquiries by model interest.' }
    ],
    testimonial: {
      quote: "Line-down calls from a Ford plant can happen at 2am. Harmonix routes them to the on-call engineer within minutes.",
      author: "Tom Reilly",
      business: "Great Lakes Precision Components"
    }
  },
  'minneapolis': {
    city: 'Minneapolis',
    state: 'MN',
    region: 'Midwest',
    population: '430K',
    businesses: '30K+',
    headline: 'AI Service for Minneapolis\'s Fortune 500 & Medical Device Economy',
    subheadline: 'Home to Target, UnitedHealth, Medtronic and 17 Fortune 500 HQs, Twin Cities businesses use Harmonix to manage enterprise vendor relationships and the world\'s largest medical device cluster.',
    localStats: { avgMessageVolume: '1,050+', missedRevenue: '$9,000/mo', avgResponseTime: '36 min' },
    industries: ['Medical Devices', 'Retail Corporate', 'Healthcare Insurance', 'Agribusiness', 'Banking'],
    industryDetails: [
      { name: 'Medical Device', challenge: 'Hospital purchasing, clinical trial enrollment, and physician education inquiries overlap.', solution: 'AI routes clinicians separately from procurement, captures credentials, and books demos with sales specialists.' },
      { name: 'Corporate Vendors', challenge: 'Competing for Target, Best Buy and UnitedHealth contracts requires sharp qualification.', solution: 'AI captures RFP type, company size and deadlines, routes enterprise opportunities to BD directors.' },
      { name: 'Agribusiness', challenge: 'Co-ops, farmers and equipment dealers call with seasonal urgency.', solution: 'AI handles seasonal scheduling, captures crop or equipment specifics, routes to appropriate specialists.' }
    ],
    testimonial: {
      quote: "We pitch Medtronic, Boston Scientific and Abbott weekly. Harmonix handles technical intake so our reps walk into every meeting prepared.",
      author: "David Schultz",
      business: "Twin Cities MedDevice Consulting"
    }
  },
  'charlotte': {
    city: 'Charlotte',
    state: 'NC',
    region: 'Southeast',
    population: '880K',
    businesses: '34K+',
    headline: 'AI Assistant for Charlotte\'s Banking Capital & Energy Hub',
    subheadline: 'Second-largest US banking centre and home to Duke Energy, Charlotte businesses use Harmonix to manage complex financial services, energy sector operations, and the fastest-growing metro in the Southeast.',
    localStats: { avgMessageVolume: '1,100+', missedRevenue: '$8,500/mo', avgResponseTime: '38 min' },
    industries: ['Banking & Finance', 'Energy & Utilities', 'Professional Services', 'Healthcare', 'Commercial Real Estate'],
    industryDetails: [
      { name: 'Banking Services', challenge: 'Bank of America and Truist generate constant vendor, client and advisor communications.', solution: 'AI routes wealth, retail and commercial inquiries, captures account context, escalates compliance issues.' },
      { name: 'Energy Sector', challenge: 'Duke Energy contractors and solar startups manage complex regulatory and RFP cycles.', solution: 'AI captures regulatory scope, project timelines and territory, routes to proper solutions teams.' },
      { name: 'Relocation', challenge: 'Corporate moves from the Northeast drive surging home-services and professional-services demand.', solution: 'AI qualifies relocation timelines, captures family needs, books services across movers, realtors and financial advisors.' }
    ],
    testimonial: {
      quote: "Bank of America vendor approvals happen fast when opportunity knocks. Harmonix captures every RFP detail so we respond before competitors.",
      author: "Jennifer Wright",
      business: "Uptown Banking Solutions"
    }
  },
  'tampa': {
    city: 'Tampa',
    state: 'FL',
    region: 'Southeast',
    population: '400K',
    businesses: '36K+',
    headline: 'AI Solutions for Tampa Bay\'s Healthcare, Defense & Tourism Economy',
    subheadline: 'MacDill AFB contractors, Moffitt Cancer Center, and Gulf Coast tourism operators use Harmonix to handle military healthcare, medical tourism and seasonal demand across West-Central Florida.',
    localStats: { avgMessageVolume: '950+', missedRevenue: '$7,200/mo', avgResponseTime: '40 min' },
    industries: ['Healthcare', 'Defense Contracting', 'Tourism', 'Financial Services', 'Cybersecurity'],
    industryDetails: [
      { name: 'Medical Specialists', challenge: 'Moffitt and BayCare receive patient referrals from across the Southeast and international sources.', solution: 'Bilingual AI handles referrals, captures insurance and records requirements, books appropriate consults.' },
      { name: 'Defense & Intel', challenge: 'CENTCOM and SOCOM contractors need clearance-aware, security-conscious intake.', solution: 'AI handles cleared-work inquiries professionally, captures contract vehicles, routes by command.' },
      { name: 'Tourism', challenge: 'Cruise passenger bookings, beach rentals and hospitality all peak simultaneously.', solution: 'AI handles multilingual bookings, captures group sizes and dates, coordinates with local operators.' }
    ],
    testimonial: {
      quote: "Cancer patients travel to Tampa from across Latin America. Harmonix handles Spanish-language intake so nothing is lost in translation.",
      author: "Dr. Carlos Ramirez",
      business: "Bay Area Oncology Group"
    }
  },
  'orlando': {
    city: 'Orlando',
    state: 'FL',
    region: 'Southeast',
    population: '310K',
    businesses: '32K+',
    headline: 'AI Never Sleeps for Orlando\'s Theme Park & Convention Economy',
    subheadline: 'Walt Disney World, Universal, and the Orange County Convention Center drive 75M visitors annually. Orlando hospitality and service businesses use Harmonix to handle global, 24/7 booking demand.',
    localStats: { avgMessageVolume: '1,400+', missedRevenue: '$9,500/mo', avgResponseTime: '34 min' },
    industries: ['Hospitality', 'Theme Park Services', 'Convention Services', 'Modeling & Simulation', 'Vacation Rentals'],
    industryDetails: [
      { name: 'Vacation Rentals', challenge: 'Families from Europe and Latin America book months ahead, often in multiple languages.', solution: 'Multilingual AI handles bookings, captures dates and party composition, coordinates check-ins.' },
      { name: 'Convention Services', challenge: 'IAAPA, PGA Show and dozens of massive conventions generate surge demand with lead-capture chaos.', solution: 'AI captures booth leads at scale, qualifies by company, schedules follow-ups post-show.' },
      { name: 'Theme Park Vendors', challenge: 'Disney and Universal procurement cycles are long, competitive and technically demanding.', solution: 'AI captures vendor credentials and product categories, routes to appropriate category managers.' }
    ],
    testimonial: {
      quote: "During IAAPA we collect thousands of leads. Harmonix qualifies them into hot prospects while the show is still running.",
      author: "Sarah O\'Connor",
      business: "Orlando Attraction Supply Co."
    }
  },
  'sacramento': {
    city: 'Sacramento',
    state: 'CA',
    region: 'West Coast',
    population: '520K',
    businesses: '28K+',
    headline: 'AI Service for Sacramento\'s Government & Ag-Tech Economy',
    subheadline: 'California\'s state capital and the Central Valley\'s ag-tech hub use Harmonix to handle government contracting, healthcare systems, and the nation\'s most important agricultural economy.',
    localStats: { avgMessageVolume: '850+', missedRevenue: '$6,200/mo', avgResponseTime: '38 min' },
    industries: ['State Government Contracting', 'Healthcare Systems', 'Agriculture Tech', 'Clean Energy', 'Legal Services'],
    industryDetails: [
      { name: 'Government Contractors', challenge: 'CA state RFPs are complex, deadline-driven and require detailed technical response.', solution: 'AI captures RFP number, scope and deadline, routes to proposal teams with full context.' },
      { name: 'Ag-Tech', challenge: 'Growers, co-ops and equipment dealers need season-aware service and technical support.', solution: 'AI captures crop type, acreage and season context, routes to field specialists during planting and harvest.' },
      { name: 'Healthcare Systems', challenge: 'Kaiser, Sutter and UC Davis Health generate enormous vendor and patient-referral volume.', solution: 'AI routes referrals by specialty, captures vendor qualifications, and books procurement meetings.' }
    ],
    testimonial: {
      quote: "CA state RFPs come with hard deadlines. Harmonix captures every requirement so our proposal team never misses a detail.",
      author: "Mark Chen",
      business: "Capitol Technology Solutions"
    }
  },
  'pittsburgh': {
    city: 'Pittsburgh',
    state: 'PA',
    region: 'Northeast',
    population: '300K',
    businesses: '22K+',
    headline: 'AI for Pittsburgh\'s Robotics, Healthcare & Steel-City Legacy',
    subheadline: 'Carnegie Mellon spin-outs, UPMC, and a resurgent manufacturing base use Harmonix to manage research partnerships, medical referrals, and the world\'s densest concentration of robotics and AI talent.',
    localStats: { avgMessageVolume: '900+', missedRevenue: '$7,000/mo', avgResponseTime: '38 min' },
    industries: ['Robotics & AI', 'Healthcare Systems', 'Advanced Manufacturing', 'Energy', 'Financial Services'],
    industryDetails: [
      { name: 'Robotics Startups', challenge: 'Autonomous vehicle, defense robotics and industrial automation leads arrive from around the world.', solution: 'AI captures technical requirements, qualifies by use case, routes to solutions engineers.' },
      { name: 'UPMC Healthcare', challenge: 'One of the largest US academic medical centres generates complex referral and research inquiries.', solution: 'AI routes by specialty, captures protocol questions, and coordinates with study teams.' },
      { name: 'Energy Sector', challenge: 'Marcellus shale operators and grid companies need rapid vendor and operational response.', solution: 'AI captures site location, equipment and urgency, dispatching to on-call field teams.' }
    ],
    testimonial: {
      quote: "We licence CMU spin-off robotics tech. Harmonix qualifies partnership inquiries so our small team only talks to the right companies.",
      author: "Dr. Amit Singh",
      business: "Oakland Robotics Labs"
    }
  },
  'cincinnati': {
    city: 'Cincinnati',
    state: 'OH',
    region: 'Midwest',
    population: '310K',
    businesses: '24K+',
    headline: 'AI Assistant for Cincinnati\'s Fortune 500 & Consumer-Brand Capital',
    subheadline: 'P&G, Kroger, Fifth Third and 10 Fortune 500 HQs call Cincinnati home. Local agencies, suppliers and consultants use Harmonix to manage the enterprise pitch economy.',
    localStats: { avgMessageVolume: '800+', missedRevenue: '$6,500/mo', avgResponseTime: '40 min' },
    industries: ['Consumer Goods', 'Retail HQ', 'Financial Services', 'Healthcare', 'Logistics'],
    industryDetails: [
      { name: 'Agency Services', challenge: 'Pitching P&G brands means fast turns and deeply qualified briefs.', solution: 'AI captures brand, category, budget and timeline, routes to the right account director for pitch response.' },
      { name: 'Retail Partners', challenge: 'Kroger and Macy\'s vendor opportunities have category-manager-specific requirements.', solution: 'AI captures SKU, category and distribution requests, routes to category specialists.' },
      { name: 'Insurance & Banking', challenge: 'Fifth Third and Cincinnati Insurance have complex agent, claim and commercial lines inquiries.', solution: 'AI separates agent support from customer claims, captures policy data, escalates urgent losses.' }
    ],
    testimonial: {
      quote: "We pitch P&G brands constantly. Harmonix captures every RFP so our creative team sees complete context before the first meeting.",
      author: "Lisa Martinez",
      business: "Queen City Agency"
    }
  },
  'cleveland': {
    city: 'Cleveland',
    state: 'OH',
    region: 'Midwest',
    population: '370K',
    businesses: '22K+',
    headline: 'AI for Cleveland\'s Medical Center & Industrial Manufacturing',
    subheadline: 'Cleveland Clinic ranks #2 globally. Combined with University Hospitals and a robust manufacturing base, Cleveland businesses use Harmonix to handle international medical tourism and B2B industrial demand.',
    localStats: { avgMessageVolume: '900+', missedRevenue: '$7,500/mo', avgResponseTime: '38 min' },
    industries: ['Medical Centers', 'Industrial Manufacturing', 'Polymers & Chemicals', 'Insurance', 'Professional Services'],
    industryDetails: [
      { name: 'Medical Tourism', challenge: 'International patients fly to Cleveland Clinic; coordination spans language, visas and complex scheduling.', solution: 'Multilingual AI handles international patient intake, captures records requests, coordinates with case managers.' },
      { name: 'Industrial B2B', challenge: 'Polymer producers, steel processors and tool makers handle urgent OEM quality and supply inquiries.', solution: 'AI triages by urgency, captures lot and part numbers, routes line-down issues to operations.' },
      { name: 'Insurance', challenge: 'Progressive and regional carriers manage claims, agents and underwriting simultaneously.', solution: 'AI separates claim-severity inquiries from agent support and commercial lines.' }
    ],
    testimonial: {
      quote: "Patients from the Middle East and Asia book Cleveland Clinic procedures through us. Harmonix manages every language seamlessly.",
      author: "Dr. Hussein Al-Khatib",
      business: "Global Medical Concierge Services"
    }
  },
  'kansas-city': {
    city: 'Kansas City',
    state: 'MO',
    region: 'Midwest',
    population: '510K',
    businesses: '26K+',
    headline: 'AI Solutions for Kansas City\'s Logistics & Animal Health Hub',
    subheadline: 'The intersection of major rail, trucking and air freight routes plus the world\'s largest concentration of animal health firms means KC businesses use Harmonix to manage 24/7 logistics and specialised B2B demand.',
    localStats: { avgMessageVolume: '950+', missedRevenue: '$6,800/mo', avgResponseTime: '40 min' },
    industries: ['Logistics & Supply Chain', 'Animal Health', 'Engineering Services', 'Financial Services', 'Tech & SaaS'],
    industryDetails: [
      { name: 'Logistics', challenge: '3PL providers, carriers and shippers generate round-the-clock capacity and claims inquiries.', solution: 'AI captures load details, origin/destination and urgency, routes capacity searches and claims separately.' },
      { name: 'Animal Health Corridor', challenge: 'Veterinary practices, pharma reps and distributors coordinate across huge geographic territories.', solution: 'AI handles scheduling, captures veterinary-specific details, routes to appropriate field reps.' },
      { name: 'Engineering Firms', challenge: 'Bridge, rail and energy projects require highly qualified intake and technical clarification.', solution: 'AI captures scope, jurisdiction and timeline, routes to the right discipline lead.' }
    ],
    testimonial: {
      quote: "Load boards go crazy at night. Harmonix captures every shipper call so our dispatchers can match capacity by 6am.",
      author: "Robert Kimball",
      business: "Heartland Freight Solutions"
    }
  },
  'melbourne': {
    city: 'Melbourne',
    state: 'Australia',
    region: 'Asia Pacific',
    population: '5.1M',
    businesses: '220K+',
    headline: 'AI Assistant for Melbourne\'s Financial, Education & Events Economy',
    subheadline: 'Australia\'s financial capital, largest university sector and the world\'s most livable city runs on Harmonix — managing international student inquiries, professional services, and major events like the Australian Open and F1 GP.',
    localStats: { avgMessageVolume: '1,600+', missedRevenue: '$10,500/mo AUD', avgResponseTime: '38 min' },
    industries: ['Banking & Finance', 'Higher Education', 'Professional Services', 'Major Events', 'Healthcare'],
    industryDetails: [
      { name: 'Universities', challenge: 'Melbourne Uni, Monash and RMIT recruit students globally; inquiries arrive 24/7 in many languages.', solution: 'Multilingual AI handles admissions questions, captures program and country details, books with advisors.' },
      { name: 'Financial Services', challenge: 'CBD firms coordinate with London, NY and Asian offices around the clock.', solution: 'AI handles international client inquiries professionally, routes by relationship manager and region.' },
      { name: 'Major Events', challenge: 'Australian Open, F1 and AFL Grand Final generate huge hospitality and vendor demand.', solution: 'AI handles corporate bookings at scale, captures hospitality needs, coordinates with event teams.' }
    ],
    testimonial: {
      quote: "We receive student inquiries from India, China and Indonesia all night. Harmonix responds in their language so they feel welcomed from the first message.",
      author: "Dr. Sarah Wilson",
      business: "Melbourne International Education Services"
    }
  },
  'vancouver': {
    city: 'Vancouver',
    state: 'Canada',
    region: 'North America',
    population: '680K',
    businesses: '130K+',
    headline: 'AI Service for Vancouver\'s Film, Tech & Asia-Pacific Gateway',
    subheadline: 'Hollywood North\'s production economy, a booming tech corridor, and deep ties to Asia-Pacific mean Vancouver businesses use Harmonix to manage multilingual, time-zone-bridging client service at scale.',
    localStats: { avgMessageVolume: '1,400+', missedRevenue: '$9,500/mo CAD', avgResponseTime: '36 min' },
    industries: ['Film & TV Production', 'Tech & SaaS', 'Real Estate', 'Tourism', 'Resource Extraction'],
    industryDetails: [
      { name: 'Film Production', challenge: 'Hollywood projects shoot on tight schedules with constant vendor, talent and location requests.', solution: 'AI handles location scout inquiries, captures equipment rental requests, routes casting submissions.' },
      { name: 'Real Estate', challenge: 'International buyers from Hong Kong, Korea and mainland China drive luxury demand.', solution: 'Multilingual AI handles Mandarin, Cantonese and Korean inquiries, captures investment criteria.' },
      { name: 'Tech Corridor', challenge: 'Microsoft, Amazon and Mozilla offices plus local startups compete fiercely for talent.', solution: 'Enterprise-grade AI creates credible presence for startups pitching enterprise clients and top engineers.' }
    ],
    testimonial: {
      quote: "Our realty firm serves buyers from Shanghai to Seoul. Harmonix handles every call in their preferred language without missing a detail.",
      author: "William Chen",
      business: "Pacific Rim Properties, Downtown Vancouver"
    }
  },
  'amsterdam': {
    city: 'Amsterdam',
    state: 'Netherlands',
    region: 'Europe',
    population: '920K',
    businesses: '90K+',
    headline: 'AI Assistant for Amsterdam\'s European HQ & Fintech Economy',
    subheadline: 'Zuidas financial district and the world\'s most concentrated European HQ location, Amsterdam businesses use Harmonix to deliver multilingual service across the EU\'s most digitally-mature economy.',
    localStats: { avgMessageVolume: '1,300+', missedRevenue: '€8,500/mo', avgResponseTime: '34 min' },
    industries: ['European Headquarters', 'Fintech & Payments', 'Creative Industries', 'Logistics', 'Legal Services'],
    industryDetails: [
      { name: 'EU Headquarters', challenge: 'Multinationals running EU operations coordinate across 27 countries and languages.', solution: 'Multilingual AI handles English, Dutch, German and French, routes by country and function.' },
      { name: 'Fintech', challenge: 'Adyen, Mollie and Klarna compete in a dense market where enterprise demos define quarterly revenue.', solution: 'AI qualifies enterprise fintech leads, captures volume requirements, books demos with solution engineers.' },
      { name: 'Legal Services', challenge: 'Zuidas firms handle pan-European M&A, tax structures and private equity matters.', solution: 'AI handles client intake with appropriate formality in multiple languages, captures matter scope.' }
    ],
    testimonial: {
      quote: "Our payments platform receives enterprise inquiries from every EU capital. Harmonix handles each in the caller\'s language — professionally.",
      author: "Marieke van Dijk",
      business: "Zuidas Payment Solutions"
    }
  },
  'frankfurt': {
    city: 'Frankfurt',
    state: 'Germany',
    region: 'Europe',
    population: '760K',
    businesses: '75K+',
    headline: 'AI Service for Frankfurt\'s Banking & European Trade Centre',
    subheadline: 'ECB headquarters, Deutsche Bank, and Europe\'s busiest cargo airport mean Frankfurt businesses use Harmonix to manage institutional finance, trade fair logistics, and precise German business standards.',
    localStats: { avgMessageVolume: '1,200+', missedRevenue: '€9,000/mo', avgResponseTime: '32 min' },
    industries: ['Banking & Finance', 'Trade Fairs & Logistics', 'Legal & Tax', 'Automotive Suppliers', 'Insurance'],
    industryDetails: [
      { name: 'Institutional Banking', challenge: 'Client teams coordinate with London, Zürich, NY and Singapore under ECB regulatory oversight.', solution: 'Bilingual AI handles English and German inquiries, captures KYC-aware intake, routes by relationship.' },
      { name: 'Trade Fair Economy', challenge: 'Messe Frankfurt events generate millions of visitors and intense vendor/hospitality demand.', solution: 'Multilingual AI handles fair registration questions, captures exhibitor needs, coordinates accommodation.' },
      { name: 'Automotive', challenge: 'Tier-1 suppliers to BMW, Mercedes and VW require technically-deep, punctual responses.', solution: 'AI captures part, program and quality urgency, escalating line-down issues to engineers immediately.' }
    ],
    testimonial: {
      quote: "Our supplier business serves every major German OEM. Harmonix answers in perfect German and routes urgent issues faster than a full-time team.",
      author: "Hans Müller",
      business: "Frankfurt Automotive Components GmbH"
    }
  }
};

const LocationPage = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const location = LOCATIONS[locationId];
  const [plans, setPlans] = useState([]);
  const { variant: heroVariant, trackConversion } = useSeoAbVariant(
    `location_hero_${locationId || 'unknown'}`,
    ['a', 'b']
  );
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [locationId]);

  useEffect(() => {
    api.get('/public/plans').then(r => setPlans(r.data || [])).catch(() => {});
  }, []);
  
  if (!location) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Location Not Found</h1>
          <p className="text-slate-600 mb-6">We don't have a dedicated page for this location yet.</p>
          <Button onClick={() => navigate('/locations')} className="bg-teal-600 hover:bg-teal-700">
            View All Locations
          </Button>
        </div>
      </div>
    );
  }
  
  const openSignUp = () => {
    navigate('/login?signup=true');
  };
  
  return (
    <>
      <Helmet>
        <title>{`AI Assistant ${location.city}, ${location.state} | Harmonix AI Employee`}</title>
        <meta name="description" content={`AI receptionist and virtual assistant for ${location.city} businesses. Handle calls, book appointments, and capture leads 24/7. Trusted by ${location.businesses} local businesses.`} />
        <meta name="keywords" content={`AI receptionist ${location.city}, virtual assistant ${location.state}, ${location.city} business automation, answering service ${location.city}`} />
        <link rel="canonical" href={`https://harmonix.info/ai-receptionist-${locationId}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={`AI Assistant for ${location.city} Businesses | Harmonix`} />
        <meta property="og:description" content={location.subheadline} />
        <meta property="og:type" content="website" />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `https://harmonix.info/ai-receptionist-${locationId}`,
            "name": `Harmonix AI Employee — ${location.city}`,
            "alternateName": `Harmonix AI Receptionist ${location.city}`,
            "description": location.subheadline,
            "url": `https://harmonix.info/ai-receptionist-${locationId}`,
            "image": LOCATION_IMAGES[locationId] || DEFAULT_CITY_IMAGE,
            "telephone": "+1-800-HARMONIX",
            "priceRange": "$$",
            "areaServed": {
              "@type": "City",
              "name": location.city,
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": location.state
              }
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": location.city,
              "addressRegion": location.state
            },
            "knowsAbout": location.industries,
            "serviceType": [
              "AI Receptionist",
              "Virtual Assistant",
              "Appointment Booking",
              "Lead Qualification",
              "24/7 Customer Service",
              ...(location.industries || []).map(i => `AI for ${i}`)
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": `AI services for ${location.city} businesses`,
              "itemListElement": (location.industryDetails || []).map((d, i) => ({
                "@type": "Offer",
                "position": i + 1,
                "itemOffered": {
                  "@type": "Service",
                  "name": `AI Assistant for ${d.name} in ${location.city}`,
                  "description": `${d.challenge} ${d.solution}`,
                  "provider": { "@type": "Organization", "name": "Harmonix" }
                }
              }))
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "127",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [{
              "@type": "Review",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "author": { "@type": "Person", "name": location.testimonial?.author },
              "reviewBody": location.testimonial?.quote
            }]
          })}
        </script>

        {/* FAQ Schema for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": (location.industryDetails || []).map(d => ({
              "@type": "Question",
              "name": `How does Harmonix help ${d.name.toLowerCase()} in ${location.city}?`,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": `${d.challenge} ${d.solution}`
              }
            }))
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <PublicHeader />
        
        {/* Hero Section with City Image */}
        <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={LOCATION_IMAGES[locationId] || DEFAULT_CITY_IMAGE} 
              alt={`${location.city} skyline`}
              className="w-full h-full object-cover opacity-10"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 to-white" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto">
            <div className="text-center max-w-4xl mx-auto">
              <Badge className="mb-4 bg-slate-100 text-slate-900 border-0">
                <MapPin className="w-3 h-3 mr-1" />
                {location.city}, {location.state}
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                {location.headline}
              </h1>
              
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                {location.subheadline}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button
                  onClick={() => { trackConversion('cta_click', { position: 'hero' }); openSignUp(); }}
                  size="lg"
                  className="bg-teal-600 hover:bg-teal-700 text-lg px-8"
                  data-testid={`location-hero-cta-${heroVariant}`}
                >
                  {heroVariant === 'b' ? 'Get my AI receptionist' : 'Start Free Trial'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button variant="outline" size="lg" className="text-lg px-8" onClick={() => navigate('/use-cases')}>
                  See Demo
                </Button>
              </div>
              
              {/* Local Stats */}
              <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
                <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <p className="text-2xl font-bold text-slate-900">{location.localStats.avgMessageVolume}</p>
                  <p className="text-sm text-slate-600">Calls/mo handled</p>
                </div>
                <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <p className="text-2xl font-bold text-red-600">{location.localStats.missedRevenue}</p>
                  <p className="text-sm text-slate-600">Lost to missed messages</p>
                </div>
                <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <p className="text-2xl font-bold text-emerald-600">24/7</p>
                  <p className="text-sm text-slate-600">AI availability</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Local Industries */}
        <section className="py-16 bg-slate-50 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Popular Industries in {location.city}
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              {location.city} businesses across these industries are using Harmonix to automate customer service
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {location.industries.map((industry, idx) => (
                <Card key={`industry-${industry}-${idx}`} className="bg-white border-slate-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-4 text-center">
                    <Building2 className="w-8 h-8 text-slate-900 mx-auto mb-2" />
                    <p className="font-medium text-slate-900">{industry}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* Industry Details - Challenge & Solution */}
        {location.industryDetails && location.industryDetails.length > 0 && (
          <section className="py-16 px-4 sm:px-6 bg-white" data-testid="industry-details-section">
            <div className="max-w-7xl mx-auto">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <Badge className="mb-3 bg-teal-50 text-teal-700 border-0">
                  How Harmonix Supports {location.city}
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                  Built for {location.city}'s Dominant Industries
                </h2>
                <p className="text-lg text-slate-600">
                  We understand the specific challenges {location.city} businesses face — and solve them with AI tailored to local demand.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {location.industryDetails.map((item, idx) => (
                  <Card key={`industry-detail-${item.name}-${idx}`} className="bg-white border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all" data-testid={`industry-detail-${idx}`}>
                    <CardContent className="p-6">
                      <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-teal-600 to-cyan-600 flex items-center justify-center mb-4">
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-4">{item.name}</h3>

                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                          <p className="text-xs font-semibold uppercase tracking-wider text-red-600">The Challenge</p>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">{item.challenge}</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Harmonix Solves It</p>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">{item.solution}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Features */}
        <section className="py-16 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
              What {location.city} Businesses Get
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="bg-white border-slate-200">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                    <Phone className="w-6 h-6 text-slate-900" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">24/7 Call Handling</h3>
                  <p className="text-slate-600">
                    Never miss a message again. AI answers every inquiry instantly, even at 2 AM or during peak hours.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                    <Calendar className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Appointment Booking</h3>
                  <p className="text-slate-600">
                    AI books appointments directly into your calendar. Clients get instant confirmation.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Lead Capture & CRM</h3>
                  <p className="text-slate-600">
                    Every conversation becomes a lead. AI scores, qualifies, and organizes prospects for you.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        
        {/* Testimonial */}
        <section className="py-16 bg-teal-600 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Star className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <blockquote className="text-2xl sm:text-3xl font-medium text-white mb-6">
              "{location.testimonial.quote}"
            </blockquote>
            <div className="text-blue-100">
              <p className="font-semibold">{location.testimonial.author}</p>
              <p className="text-sm">{location.testimonial.business}</p>
            </div>
          </div>
        </section>
        
        {/* Dynamic Pricing */}
        {plans.length > 0 && (
          <section className="py-16 px-4 sm:px-6 bg-gradient-to-b from-slate-50 to-white" data-testid="location-pricing">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
                Plans for {location.city} Businesses
              </h2>
              <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
                Choose a plan that fits your {location.city} business needs
              </p>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
                {plans.slice(0, 3).map((plan, idx) => {
                  const isLowest = plan.monthly_price > 0 && plan.monthly_price === Math.min(...plans.filter(p => p.monthly_price > 0).map(p => p.monthly_price));
                  return (
                    <Card key={plan.plan_id} className={`relative bg-white border-2 transition-all hover:shadow-lg ${isLowest ? 'border-teal-600 shadow-lg ring-2 ring-slate-100' : 'border-slate-200'}`} data-testid={`location-plan-${plan.plan_id}`}>
                      {isLowest && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-teal-600 text-white px-3 py-1 text-xs"><Star className="w-3 h-3 mr-1 inline" />Best Value</Badge>
                        </div>
                      )}
                      <CardContent className="p-6 text-center">
                        <h3 className="text-lg font-bold text-slate-900 mb-1">{plan.name}</h3>
                        <p className="text-xs text-slate-500 mb-4">{plan.description}</p>
                        <div className="mb-5">
                          <span className="text-3xl font-extrabold text-slate-900">${plan.monthly_price}</span>
                          <span className="text-sm text-slate-500">/mo</span>
                        </div>
                        <ul className="space-y-2 mb-6 text-left">
                          {(plan.features || []).slice(0, 5).map((f, fi) => (
                            <li key={`${plan.plan_id}-feat-${f}-${fi}`} className="flex items-center gap-2 text-sm text-slate-700">
                              <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />{f}
                            </li>
                          ))}
                        </ul>
                        <Button onClick={() => navigate('/login?signup=true')} className={`w-full ${isLowest ? 'bg-teal-600 hover:bg-teal-700 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`} data-testid={`location-plan-cta-${plan.plan_id}`}>
                          {isLowest ? 'Start Free Trial' : 'Get Started'}
                          <ArrowRight className="w-4 h-4 ml-1.5" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
                {/* Custom Plan Card */}
                <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-0 text-white" data-testid="location-custom-plan">
                  <CardContent className="p-6 text-center flex flex-col justify-between h-full">
                    <div>
                      <Mail className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                      <h3 className="text-lg font-bold mb-1">Custom Plan</h3>
                      <p className="text-xs text-slate-400 mb-4">Tailored to your {location.city} business</p>
                      <ul className="space-y-2 mb-6 text-left">
                        {['Unlimited AI agents', 'Custom integrations', 'Dedicated support', 'SLA guarantee'].map((f) => (
                          <li key={`custom-${f}`} className="flex items-center gap-2 text-sm text-slate-300">
                            <Check className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/#pricing')} className="w-full border-slate-600 text-white hover:bg-slate-700" data-testid="location-custom-plan-cta">
                      Contact Us
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Ready to Grow Your {location.city} Business?
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Join {location.businesses} businesses in {location.city} using AI to handle customer service.
            </p>
            <Button onClick={openSignUp} size="lg" className="bg-teal-600 hover:bg-teal-700 text-lg px-8">
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-sm text-slate-500 mt-4">No credit card required. Setup in 5 minutes.</p>
          </div>
        </section>
        
        {/* Other Locations */}
        <section className="py-16 bg-slate-50 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Also Serving These Cities
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {Object.entries(LOCATIONS)
                .filter(([id]) => id !== locationId)
                .slice(0, 6)
                .map(([id, loc]) => (
                  <Link
                    key={id}
                    to={`/ai-receptionist/${id}`}
                    className="px-4 py-2 bg-white border border-slate-200 rounded-full text-slate-700 hover:border-slate-300 hover:text-slate-900 transition-colors"
                  >
                    {loc.city}, {loc.state}
                  </Link>
                ))}
            </div>
          </div>
        </section>
        
        <PublicFooter />
      </div>
    </>
  );
};

export default LocationPage;
